import React, { useEffect } from 'react';
import { useHistory, useLocation } from 'react-router';
import { ICompany } from 'types/company';
import CompanyConversationDialogAsUser, { useCanContactCompanyAsSelf } from './CompanyConversationDialogAsUser';
import useSendAs, { useEntityInboxUrl } from './sendAs';

interface Props {
  company: ICompany;
  onClose: () => void;
  initialMessage?: string;
  /**
   * For actions that only make sense in your own name, whatever hat the surrounding page puts on you
   * — "I'm interested" on a funding round is you as an investor, not your community.
   */
  writeAsSelf?: boolean;
}

/**
 * What "Contact" means for a company recipient. Inside a community or company you administer the
 * conversation belongs to that entity, so this sends you to its inbox rather than opening a dialog:
 * there the thread has a url, sits beside the entity's other conversations, survives a reload, and
 * carries the picker for dropping back to writing personally. Everyone else keeps the dialog.
 */
export default function ConversationWithCompany({ company, onClose, initialMessage, writeAsSelf }: Props) {
  const history = useHistory();
  const location = useLocation();

  // Writing to a company you administer as yourself is refused further down, so don't let that be
  // the identity we resolve to — it would open a dialog with no reply field in it.
  const canWriteAsSelf = useCanContactCompanyAsSelf(company.slug);
  const sendAs = useSendAs({ excludeId: company.id, allowUser: canWriteAsSelf });
  const inboxUrl = useEntityInboxUrl(writeAsSelf ? undefined : { type: 'company', companyId: company.id });

  // Navigating rather than rendering keeps every contact button as it is: they still just flip a
  // boolean and mount this, and the push unmounts it again. Contact buttons also live inside the
  // thread itself (the profile dialog you reach from a message), so landing on the url we are
  // already on just dismisses whatever opened us instead of stacking a history entry.
  const goToInbox = sendAs.selected.type === 'company' && !!inboxUrl;
  useEffect(() => {
    if (!goToInbox || !inboxUrl) return;

    if (location.pathname === inboxUrl) {
      onClose();
    } else {
      history.push(inboxUrl);
    }
  }, [goToInbox, inboxUrl, location.pathname]);

  if (goToInbox) {
    return null;
  }

  return <CompanyConversationDialogAsUser company={company} onClose={onClose} initialMessage={initialMessage} />;
}
